import React, { memo, useState, useEffect, useRef } from 'react';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { getBookInfo } from '../../utils/bibleData';

const TopNavigation = memo(({
  selectedBook,
  selectedChapter,
  startVerse,
  endVerse,
  selectedPlan,
  availableListeningPlans,
  currentListeningPlan,
  isLoadingRecordings,
  showNotesDrawer,
  onTogglePassageSelector,
  onToggleBottomSheet,
  onToggleNotesDrawer,
  onNavigateToPreviousPlanChapter,
  onNavigateToNextPlanChapter,
  onSetSelectedPlan,
}) => {
  const [planDropdownOpen, setPlanDropdownOpen] = useState(false);
  const planDropdownRef = useRef(null);

  useEffect(() => {
    if (!planDropdownOpen) return;
    function handleClick(e) {
      if (planDropdownRef.current && !planDropdownRef.current.contains(e.target)) {
        setPlanDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [planDropdownOpen]);

  // Active plan: 'none' = explicitly no plan; specific id = that plan; 'all' = follow subscription
  const activePlan = selectedPlan === 'none'
    ? null
    : selectedPlan !== 'all'
    ? availableListeningPlans?.find(p => p.id === selectedPlan)
    : currentListeningPlan;

  const planName = activePlan?.name || null;

  const planChapterLabel = (() => {
    if (!activePlan?.chapters) return null;
    const idx = activePlan.chapters.findIndex(
      c => c.book === selectedBook && c.chapter === selectedChapter
    );
    if (idx < 0) return null;
    return `Ch ${idx + 1} / ${activePlan.chapters.length}`;
  })();

  return (
    <>
      <style>{`
        .top-nav {
          top: 0;
          z-index: 9999;
        }
        @media (min-width: 1024px) {
          .top-nav {
            top: 64px;
            z-index: 9998;
          }
        }
      `}</style>
      <div className="bg-white dark:bg-[#0d1520] border-b border-gray-200/50 dark:border-white/10 shadow-sm dark:shadow-none fixed left-0 right-0 transform translate-z-0 will-change-transform top-nav">
        <div className="max-w-6xl mx-auto px-4 py-3">
          {/* Main row: passage + controls */}
          <div className="flex items-center justify-between">
            {/* Left: Bible icon + book + chapter */}
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <button
                onClick={onTogglePassageSelector}
                className="p-1.5 rounded-xl text-cerulean hover:text-primary-700 transition-all duration-200 active:scale-95 flex-shrink-0"
                title="Select passage"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
                </svg>
              </button>
              <span className="text-sm font-medium text-space-cadet dark:text-white whitespace-nowrap">
                {getBookInfo(selectedBook)?.name}
              </span>
              <span className="text-sm text-gray-600 dark:text-white/50 whitespace-nowrap">
                {selectedChapter}
                {startVerse && endVerse && (
                  startVerse !== endVerse ? `:${startVerse}-${endVerse}` : `:${startVerse}`
                )}
              </span>
            </div>

            {/* Right: spinner + filter + notes + play (desktop only) */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              {isLoadingRecordings && (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-cerulean border-t-transparent" />
              )}
              {/* Opens the bottom player sheet, on every screen size */}
              <button
                onClick={onToggleBottomSheet}
                className="p-1.5 rounded-xl transition-all duration-200 active:scale-95 text-gray-600 dark:text-white/60 hover:text-gray-800 dark:hover:text-white"
                title="Listening settings"
              >
                <AdjustmentsHorizontalIcon className="w-5 h-5" />
              </button>
              <button
                className="px-3 py-2 text-sm text-space-cadet dark:text-white/80 hover:text-cerulean dark:hover:text-cerulean-lighter transition-colors font-medium whitespace-nowrap"
                onClick={onToggleNotesDrawer}
              >
                Notes
              </button>
            </div>
          </div>

          {/* Plan strip — clickable dropdown */}
          {availableListeningPlans?.length > 0 && (
            <div className="flex items-center justify-center gap-3 mt-1">
              <div className="relative" ref={planDropdownRef}>
                <button
                  onClick={() => setPlanDropdownOpen(o => !o)}
                  className="flex items-center gap-1 text-xs text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70 transition-colors"
                >
                  <span className="tracking-wide">{planName || 'Select plan'}</span>
                  <svg
                    className={`w-3 h-3 transition-transform ${planDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none" stroke="currentColor" viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {planDropdownOpen && (
                  <div className="absolute top-full mt-1 left-1/2 -translate-x-1/2 z-50 bg-white dark:bg-[#111827] rounded-xl shadow-lg border border-gray-200 dark:border-white/10 py-1 min-w-max">
                    <button
                      onClick={() => { onSetSelectedPlan?.('none'); setPlanDropdownOpen(false); }}
                      className={`w-full text-left px-4 py-2 text-xs transition-colors ${
                        selectedPlan === 'none'
                          ? 'text-cerulean font-semibold bg-cerulean/5'
                          : 'text-gray-500 dark:text-white/50 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                    >
                      No plan
                    </button>
                    {availableListeningPlans.map(plan => (
                      <button
                        key={plan.id}
                        onClick={() => { onSetSelectedPlan?.(plan.id); setPlanDropdownOpen(false); }}
                        className={`w-full text-left px-4 py-2 text-xs transition-colors ${
                          activePlan?.id === plan.id
                            ? 'text-cerulean font-semibold bg-cerulean/5'
                            : 'text-gray-500 dark:text-white/50 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
                        }`}
                      >
                        {plan.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {planChapterLabel && (
                <>
                  <span className="text-gray-300 dark:text-white/20 text-xs">·</span>
                  <span className="text-xs text-gray-400 dark:text-white/40 tracking-wide">{planChapterLabel}</span>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
});

TopNavigation.displayName = 'TopNavigation';

export default TopNavigation;
